import { EquipmentCertificationsDonutChart } from '@frontend/chart/components';
import { Chart } from '@frontend/chart/types';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { StatisticsCard } from '../../shared';
import { useLendingEquipmentCertificationsDonutChart } from './use-lending-equipment-certifications-donut-chart';

export interface LendingEquipmentCertificationsDonutChartProps {
    accountId: string;
}

const localizeCertificationLabel = (rawLabel: string, labels: { certified: string; expiresSoon: string; expired: string }): string => {
    const normalized = rawLabel.trim().toLowerCase().replace(/\s+/g, '_');

    if (normalized === 'certified') {
        return labels.certified;
    }

    if (normalized === 'expires_soon') {
        return labels.expiresSoon;
    }

    if (normalized === 'expired') {
        return labels.expired;
    }

    return rawLabel.replace(/_/g, ' ');
};

const localizeCertificationChart = (chart: Chart, labels: { certified: string; expiresSoon: string; expired: string }): Chart => ({
    series: chart.series.map((series) => {
        const rawName = series.name ?? series.data?.[0]?.label ?? '';
        const displayName = localizeCertificationLabel(rawName, labels);

        return {
            ...series,
            name: displayName,
            data: series.data.map((point) => ({
                ...point,
                label: localizeCertificationLabel(String(point.label ?? rawName), labels)
            }))
        };
    })
});

export const LendingEquipmentCertificationsDonutChart = (props: LendingEquipmentCertificationsDonutChartProps) => {
    const { formatMessage } = useIntl();
    const viewProps = useLendingEquipmentCertificationsDonutChart(props);

    const statusLabels = useMemo(
        () => ({
            certified: formatMessage({
                id: 'certificate.status.certified',
                defaultMessage: 'Certified'
            }),
            expiresSoon: formatMessage({
                id: 'platform.statistics.lending.lending-equipment-certifications-donut-chart.expires-soon',
                description: 'Legend label for equipment certifications that expire soon on the lending dashboard',
                defaultMessage: 'Expires soon'
            }),
            expired: formatMessage({
                id: 'certificate.status.expired',
                defaultMessage: 'Expired'
            })
        }),
        [formatMessage]
    );

    const data = useMemo(() => (viewProps.data ? localizeCertificationChart(viewProps.data, statusLabels) : undefined), [viewProps.data, statusLabels]);

    return (
        <StatisticsCard
            chartKey={EquipmentCertificationsDonutChart.definition.key}
            title={
                <FormattedMessage
                    id='platform.statistics.lending.lending-equipment-certifications-donut-chart.title'
                    description='Title of the lending dashboard chart showing equipment certification status counts'
                    defaultMessage='Equipment certifications'
                />
            }
            isLoading={viewProps.isLoading}
            hasData={!!data}>
            {data && (
                <div className='mt-auto h-full min-h-0 w-full flex-1'>
                    <EquipmentCertificationsDonutChart
                        data={data}
                        minHeight={80}
                        onSliceClick={() => viewProps.navigateToCertifiedEquipments()}
                    />
                </div>
            )}
        </StatisticsCard>
    );
};
